import { useState } from "react";
import { Globe, Loader2, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const FIELDS = [
  ["company_name", "Name"], ["tagline", "Tagline"], ["industry", "Industry"], ["mission", "Mission"],
  ["vision", "Vision"], ["values", "Values"], ["origin_story", "Origin story"], ["target_audience", "Target audience"],
];
const show = (v) => (Array.isArray(v) ? v.join(", ") : v || "");
const valueNames = (vals) => vals.map((v) => v.split(" — ")[0].replace(/\s*,\s*(and\s+|&\s+)?/g, " & ").trim()).filter(Boolean);

export const WebsiteDraft = ({ current, onApply }) => {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [draft, setDraft] = useState(null);
  const [pick, setPick] = useState({});

  const close = () => { setOpen(false); setDraft(null); setErr(""); };

  const run = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/profile/draft-from-website", { url });
      const d = { ...data.draft, values: valueNames(data.draft.values) };
      setDraft({ ...data, draft: d });
      setPick(Object.fromEntries(FIELDS.map(([k]) => [k, !!show(d[k]) && !show(current[k])])));
    } catch (e) { const d = e.response?.data?.detail; setErr(d?.message || (typeof d === "string" ? d : "Couldn't draft from that website")); }
    finally { setBusy(false); }
  };

  const apply = () => {
    const chosen = Object.fromEntries(FIELDS.filter(([k]) => pick[k]).map(([k]) => [k, draft.draft[k]]));
    onApply(chosen);
    toast.success("Draft added. Edit anything, then Save profile.");
    close();
  };

  const n = Object.values(pick).filter(Boolean).length;
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="website-draft-open"
        className="border border-zinc-900 px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-zinc-900 hover:text-white inline-flex items-center gap-2 transition-colors">
        <Globe className="w-3.5 h-3.5" /> Draft from my website
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-zinc-900/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="website-draft-modal">
          <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 sm:p-8">
            <div className="flex items-start justify-between gap-4 mb-2">
              <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#0033FF]">Optional head start</div>
              <button onClick={close} data-testid="website-draft-close" aria-label="Close"><X className="w-4 h-4 text-zinc-400 hover:text-zinc-900" /></button>
            </div>
            <h2 className="text-2xl font-black tracking-tighter mb-1">Draft your profile from your website</h2>
            <p className="text-xs text-zinc-500 mb-5">Websites go stale, and your vision moves faster than your site. Treat this as a first draft: you pick what to use and can edit every word afterward. Nothing is saved until you press Save profile.</p>
            {!draft ? (
              <form onSubmit={run} className="flex flex-col sm:flex-row gap-2">
                <input data-testid="website-draft-url" value={url} onChange={(e) => setUrl(e.target.value)} required placeholder="yourcompany.com or your About page"
                  className="flex-1 px-3 py-3 border border-zinc-300 focus:outline-none focus:border-[#0033FF] text-sm" />
                <button disabled={busy} data-testid="website-draft-submit"
                  className="bg-[#0033FF] text-white px-5 py-3 text-xs font-bold uppercase tracking-wider hover:bg-zinc-900 disabled:opacity-50 inline-flex items-center justify-center gap-2">
                  {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Reading site…</> : <><Sparkles className="w-3.5 h-3.5" /> Draft it</>}
                </button>
              </form>
            ) : (
              <>
                <div className="text-xs text-zinc-500 mb-3">From <span className="font-mono">{draft.source_url}</span>. Fields you've already filled in start unticked.</div>
                <div className="border border-zinc-200 divide-y divide-zinc-200 mb-5">
                  {FIELDS.filter(([k]) => show(draft.draft[k])).map(([k, label]) => (
                    <label key={k} className="flex gap-3 p-4 cursor-pointer hover:bg-zinc-50" data-testid={`website-draft-field-${k}`}>
                      <input type="checkbox" checked={!!pick[k]} onChange={(e) => setPick({ ...pick, [k]: e.target.checked })}
                        className="mt-1 accent-[#0033FF]" data-testid={`website-draft-pick-${k}`} />
                      <div className="min-w-0">
                        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">{label}{show(current[k]) && <span className="text-[#FF3B30] ml-2">replaces yours</span>}</div>
                        <div className="text-sm text-zinc-800 mt-1 leading-relaxed">{show(draft.draft[k])}</div>
                      </div>
                    </label>
                  ))}
                </div>
                <div className="flex flex-wrap gap-3 justify-end">
                  <button onClick={() => setDraft(null)} data-testid="website-draft-retry" className="border border-zinc-300 px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:border-zinc-900">Try another page</button>
                  <button onClick={apply} disabled={!n} data-testid="website-draft-apply"
                    className="bg-[#0033FF] text-white px-5 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-zinc-900 disabled:opacity-40">Use {n} field{n === 1 ? "" : "s"}</button>
                </div>
              </>
            )}
            {err && <div className="text-sm text-[#FF3B30] border-l-2 border-[#FF3B30] pl-3 py-1 mt-4" data-testid="website-draft-error">{err}</div>}
          </div>
        </div>
      )}
    </>
  );
};
